import {InfiniteData, Query, QueryKey} from '@tanstack/react-query';
import {AspectRatio} from '@contentlab/types/aspectRatio';
import assetFetchers from '@/api/fetchers/content-studio/asset';
import {Asset} from '@/views/content-studio/components/asset-table/types';
import {ASSETS, CONTENT_LAB} from '@/hooks/content-studio/consts';
import useInfiniteReactQuery from '@/hooks/use-infinite-react-query';

export type AssetsQuerySorting = {
  field: string;
  desc: boolean;
};

export type AssetsQueryFilters = {
  type: 'uploads' | 'results';
  project?: string[];
  source?: string[];
  language?: string[];
  format?: ('16:9' | '9:16' | '1:1' | '4:5')[];
  name?: string;
  ids?: string[];
  startDate?: Date;
  endDate?: Date;
};

interface UseAssetsQueryParams {
  sorting: AssetsQuerySorting;
  filters: AssetsQueryFilters;
  enabled?: boolean;
  refetchInterval:
    | number
    | false
    | ((
        data: InfiniteData<Asset[]> | undefined,
        query: Query<Asset[], unknown, InfiniteData<Asset[]>, QueryKey>
      ) => number | false);
  onSuccess: ((data: InfiniteData<Asset[]>) => void) | undefined;
  keepPreviousData?: boolean;
}

function useAssetsQuery({
  sorting,
  filters,
  enabled = true,
  refetchInterval = false,
  onSuccess = undefined,
  keepPreviousData = true,
}: UseAssetsQueryParams) {
  return useInfiniteReactQuery<Asset[]>({
    queryKey: [CONTENT_LAB, ASSETS, sorting, filters],
    queryFn: async (): Promise<Asset[]> => {
      const response = await assetFetchers.getAssets({
        sorting: {
          field: sorting.field,
          order: sorting.desc ? 'desc' : 'asc',
        },
        filters: {
          folder: filters.project,
          source: filters.source,
          language: filters.language,
          aspect_ratio: filters.format as AspectRatio[],
          name: filters.name,
          ids: filters.ids,
          type: filters.type === 'uploads' ? 'upload' : 'result',
        },
      });

      const assets = response.assets.map((asset) => ({
        ...asset,
        metadata: {
          durationMillis: asset.metadata.durationMillis,
          fileSizeBytes: asset.metadata.fileSizeBytes,
          height: asset.metadata.height,
          width: asset.metadata.width,
          language: asset.metadata.language || undefined,
          aspectRatio: asset.metadata.aspectRatio as AspectRatio,
        },
      }));

      return assets;
    },
    refetchInterval,
    enabled,
    onSuccess,
    keepPreviousData,
  });
}

export default useAssetsQuery;
